import { useState } from "react"
import { Box, IconButton, Spinner, Table } from "@chakra-ui/react"
import { FaTrash, FaTriangleExclamation } from "react-icons/fa6"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { DeleteMappingDialog } from "./DeleteMappingDialog"
import {
  computeAvailableAssets,
  isSourceStale,
  isSourceMissing,
} from "./mappingUtils"
import { filterSourcesByAssetClass, filterAssetsBySourceClass } from "@hydrologiq/shared/data-connections/sourceFilterUtils"
import { mappedSourceLabel, sourceDisplayLabel } from "@hydrologiq/shared/data-connections/sourceLabels"
import { COLOR_WARNING, COLOR_WARNING_BG } from "@hydrologiq/shared/data-connections/constants"
import { AssetTypeLabel, MappedSourceText, MappingTableHeader } from "@hydrologiq/shared/data-connections/MappingDisplay"
import type { MappedAsset, DeviceSource } from "../../hooks/useDataConnectionMapping"
import type { SystemAsset } from "../../providers/MapAssetDataProvider"
import { useMapAssetDataStatus } from "../../providers/MapAssetDataProvider"

// ─── Main component ───────────────────────────────────────────────────────────

export interface InlineMappingTableProps {
  mappedAssets: MappedAsset[]
  sources: DeviceSource[]
  systemAssets: SystemAsset[]
  isEditable: boolean
  onUpdateMapping: (assetIntegrationId: string, field: "asset" | "source", value: string) => Promise<void>
  onDeleteMapping: (assetIntegrationId: string) => void
}

export function InlineMappingTable({
  mappedAssets,
  sources,
  systemAssets,
  isEditable,
  onUpdateMapping,
  onDeleteMapping,
}: InlineMappingTableProps) {
  const [deleteTarget, setDeleteTarget] = useState<MappedAsset | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const { connectorType } = useMapAssetDataStatus()

  const handleUpdate = async (assetIntegrationId: string, field: "asset" | "source", value: string) => {
    setUpdatingId(assetIntegrationId)
    try {
      await onUpdateMapping(assetIntegrationId, field, value)
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <>
      <Table.Root variant="hydrologiq" size="sm">
        <MappingTableHeader connectorType={connectorType} />
        <Table.Body>
          {mappedAssets.map((mapping) => {
            const stale = isSourceStale(mapping.sourceRef, sources)
            const missing = isSourceMissing(mapping.sourceRef, sources)
            const sourceName = mappedSourceLabel(mapping.sourceRef, mapping.sourceLabel, sources)

            const rowStyle: React.CSSProperties = missing
              ? {
                  background: COLOR_WARNING_BG,
                  borderLeft: `3px solid ${COLOR_WARNING}`,
                }
              : {}

            return (
              <Table.Row key={mapping.id} style={rowStyle}>
                {/* Asset Name column */}
                <Table.Cell>
                  {isEditable ? (
                    <AssetSelect
                      mapping={mapping}
                      mappedAssets={mappedAssets}
                      systemAssets={systemAssets}
                      sources={sources}
                      onUpdateMapping={handleUpdate}
                    />
                  ) : (
                    mapping.assetName
                  )}
                </Table.Cell>

                {/* Asset Type column */}
                <Table.Cell>
                  <AssetTypeLabel assetClass={mapping.assetClass} />
                </Table.Cell>

                {/* Source column */}
                <Table.Cell>
                  {isEditable && !stale ? (
                    <SourceSelect
                      mapping={mapping}
                      mappedAssets={mappedAssets}
                      sources={sources}
                      onUpdateMapping={handleUpdate}
                    />
                  ) : (
                    <MappedSourceText sourceLabel={mapping.sourceLabel} sourceName={sourceName} stale={stale} />
                  )}
                </Table.Cell>

                {/* Action column */}
                <Table.Cell textAlign="end">
                  {updatingId === mapping.id ? (
                    <Spinner size="xs" />
                  ) : missing ? (
                    <Box color={COLOR_WARNING} display="inline-flex" alignItems="center">
                      <FaTriangleExclamation />
                    </Box>
                  ) : isEditable ? (
                    <IconButton
                      aria-label={`Remove ${mapping.assetName}`}
                      variant={"hydrologiq_icon" as any}
                      size="xs"
                      onClick={() => setDeleteTarget(mapping)}
                    >
                      <FaTrash />
                    </IconButton>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>

      <DeleteMappingDialog
        open={deleteTarget !== null}
        assetName={deleteTarget?.assetName ?? ""}
        sourceLabel={deleteTarget?.sourceLabel ?? ""}
        onConfirm={() => {
          if (deleteTarget) {
            onDeleteMapping(deleteTarget.id)
          }
          setDeleteTarget(null)
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  )
}

// ─── Asset Select (editable row) ─────────────────────────────────────────────

function AssetSelect({
  mapping,
  mappedAssets,
  systemAssets,
  sources,
  onUpdateMapping,
}: {
  mapping: MappedAsset
  mappedAssets: MappedAsset[]
  systemAssets: SystemAsset[]
  sources: DeviceSource[]
  onUpdateMapping: (id: string, field: "asset" | "source", value: string) => void
}) {
  const available = computeAvailableAssets(systemAssets, mappedAssets, mapping.assetId)

  // Filter assets to those that fit the mapped source
  const currentSource = sources.find((s) => s.ref === mapping.sourceRef)
  const compatibleClasses = currentSource?.compatibleClasses ?? []
  const filtered = filterAssetsBySourceClass(available, compatibleClasses, currentSource?.fittingAssetIds)

  const items = filtered.map((a) => ({ value: a.id, label: a.name }))

  // Ensure the current mapping's asset is always present (even if class filter would exclude it)
  const hasCurrentAsset = items.some((item) => item.value === mapping.assetId)
  if (!hasCurrentAsset) {
    items.unshift({ value: mapping.assetId, label: mapping.assetName })
  }

  return (
    <NativeSelectRoot size="xs">
      <NativeSelectField
        value={mapping.assetId}
        onChange={(e) => {
          if (e.target.value !== mapping.assetId) {
            onUpdateMapping(mapping.id, "asset", e.target.value)
          }
        }}
        items={items}
      />
    </NativeSelectRoot>
  )
}

// ─── Source Select (editable row) ────────────────────────────────────────────

function SourceSelect({
  mapping,
  mappedAssets,
  sources,
  onUpdateMapping,
}: {
  mapping: MappedAsset
  mappedAssets: MappedAsset[]
  sources: DeviceSource[]
  onUpdateMapping: (id: string, field: "asset" | "source", value: string) => void
}) {
  // Filter to sources that fit the asset, then exclude sources mapped in other rows
  const otherMappings = mappedAssets.filter((m) => m.id !== mapping.id)
  const classFiltered = filterSourcesByAssetClass(sources, mapping.assetClass, otherMappings, mapping.assetId)
  const availableItems = classFiltered.filter((s) => !s.disabled)

  const items: Array<{ value: string; label: string }> = []

  // If source is unassigned, show a placeholder prompt
  if (!mapping.sourceRef) {
    items.push({ value: "", label: "Select a source..." })
  }

  items.push(...availableItems.map((s) => ({ value: s.ref, label: sourceDisplayLabel(s, sources) })))

  // Ensure the current mapping's source is always present (even if it wouldn't pass the class filter)
  if (mapping.sourceRef) {
    const hasCurrentSource = items.some((item) => item.value === mapping.sourceRef)
    if (!hasCurrentSource) {
      items.unshift({ value: mapping.sourceRef, label: mapping.sourceLabel })
    }
  }

  return (
    <NativeSelectRoot size="xs">
      <NativeSelectField
        value={mapping.sourceRef}
        onChange={(e) => {
          if (e.target.value !== mapping.sourceRef) {
            onUpdateMapping(mapping.id, "source", e.target.value)
          }
        }}
        items={items}
      />
    </NativeSelectRoot>
  )
}

export default InlineMappingTable
